<script setup lang="ts">
import { useSearch } from "~/composables/search";
import SelectTag from "~/components/form/SelectTag.vue";
import { DATATYPES } from "~/constants/dataTypes";
import { computed } from "vue";
import CheckMark from "~/components/icons/CheckMark.vue";
import { useSideNavStore } from "~/store/sideNavStore";

const router = useRouter();
const route = useRoute();

// Data
const slug = (route.params.mainSchema ?? "") as string;
const { dataType, search, limit, result, loading, error, doFetch, pagination } = useSearch();
// Get dataType base on router URL
dataType.value = slug.toLowerCase(); // router.currentRoute.value.path.split("/")[1].toLowerCase();

// Get dataType base on router URL. If not found OR it's type array redirect to home page
if (!(dataType.value in DATATYPES) || dataType.value.toLowerCase() === "array") {
  router.push("/");
}
const { title, description, displayColumns, tabs, table } = DATATYPES[dataType.value];

const sideNavStore = useSideNavStore();
const useXl = computed(() => sideNavStore.expanded);
const columnWidth = computed(() => {
  switch (displayColumns.length) {
    case 2:
      return useXl.value ? "xl:w-[30%] xl:min-w-[250px]" : "lg:w-[20%] lg:min-w-[250px]";
    case 3:
      return useXl.value ? "xl:w-[25%] xl:min-w-[250px]" : "lg:w-[25%] lg:min-w-[250px]";
    case 4:
      return useXl.value ? "xl:w-[20%] xl:min-w-[250px]" : "lg:w-[20%] lg:min-w-[250px]";
    default:
      return useXl.value ? "xl:w-[10%] lg:min-w-[250px]" : "xl:w-[10%] lg:min-w-[250px]";
  }
});

const isNullable = (val: string) => {
  if (val.toLowerCase() === "nullable") {
    return true;
  }
  return false;
};

await doFetch();
</script>

<template>
  <div>
    <div class="mb-4 md:mb-10">
      <h1 class="text-3xl capitalize leading-loose md:inline-block">{{ title }}</h1>

      <p class="mb-10 font-light text-grc-gray-darker md:pl-1">
        {{ description }}
      </p>
    </div>

    <div>
      <div class="flex-row-reverse items-end md:flex">
        <SearchBox v-model="search" class="mb-2 flex-none md:ml-2 md:w-1/3" />

        <div class="flex-1 pb-2 text-right">
          <!-- Per page -->
          <SelectTag v-model="limit" class="w-full md:w-auto">
            <option v-for="val in ['10', '20', '50', '100']" :key="val" :value="val">View {{ val }} per page</option>
          </SelectTag>
        </div>

        <div class="mt-4 flex-1 md:mt-0">More specific types:</div>
      </div>

      <!-- List items -->
      <ListItems :items="result">
        <template #header>
          <div v-if="displayColumns.includes('Name')" class="mr-2 flex-none" :class="columnWidth">Name</div>

          <div v-if="displayColumns.includes('DataType')" class="mr-2 flex-none lg:w-28">DataType</div>

          <div v-if="displayColumns.includes('Nullable')" class="mr-2 flex-none lg:w-28">Nullable</div>

          <div v-if="displayColumns.includes('Description')" class="flex-1">Description</div>
        </template>

        <template #row="{ item }">
          <div
            v-if="displayColumns.includes('Name')"
            class="mr-2 flex-none font-bold lg:font-normal"
            :class="columnWidth"
          >
            {{ dataType === "thing" ? item.fieldData.camelCaseName : item.fieldData.lowerCamelName }}
            <span v-if="displayColumns.includes('DataType')" class="text-sm font-normal text-grc-gray-darker lg:hidden">
              ({{ item.fieldData.dataTypesName }})
            </span>
          </div>

          <div v-if="displayColumns.includes('DataType')" class="mr-2 hidden flex-none lg:block lg:w-28">
            {{ item.fieldData.dataTypesName }}
          </div>

          <div
            v-if="displayColumns.includes('Nullable')"
            class="mr-2 flex-none"
            :class="useXl ? 'xl:w-28 xl:pl-6' : 'lg:w-28 lg:pl-6'"
          >
            <div :class="useXl ? 'xl:hidden' : 'lg:hidden'">
              Nullable? {{ isNullable(item.fieldData.nullable) ? "Yes" : "No" }}
            </div>

            <CheckMark
              v-if="isNullable(item.fieldData.nullable)"
              class="hidden w-5"
              :class="useXl ? 'xl:block' : 'lg:block'"
            />
          </div>

          <div v-if="displayColumns.includes('Description')" class="mt-2 flex-1 lg:mt-0">
            {{ item.fieldData.description }}
          </div>
        </template>
      </ListItems>

      <!-- Pagination content -->
      <div v-if="pagination" class="mt-4 md:flex">
        <div class="mb-2 flex-1 text-center md:mb-0 md:text-left">
          Showing <b>{{ pagination.showItemStart }}</b>

          to <b>{{ pagination.showItemEnd }}</b>

          of <b>{{ pagination.totalItems }}</b> results
        </div>

        <PaginationLinks :data="pagination" class="flex-1 text-center md:text-right" />
      </div>
    </div>

    <!--<div v-if="table === 'Schema'">-->
    <!--  <h2 class="mt-16 text-2xl">Details</h2>-->

    <!--  <TabContent :tabs="tabs" class="mt-6">-->
    <!--    <template #schema>-->
    <!--      <div>Schema coming soon...</div>-->
    <!--    </template>-->

    <!--    <template #visualized>-->
    <!--      <div>Visualized coming soon...</div>-->
    <!--    </template>-->
    <!--  </TabContent>-->
    <!--</div>-->
  </div>
</template>
